import { JobStarter } from '@app/components/base/JobStarter';
import { refetchIntervalForJob } from '@app/core/jobs/refect-interval-for-job';
import { Fragment } from '@app/modules/fragments/types';
import {
  useCfrTranscodingGetCfrTranscodingJobStatus,
  useCfrTranscodingStartCfrTranscodingJob,
} from '@mi-front/api-clients';
import SwitchVideoIcon from '@mui/icons-material/SwitchVideo';
import { Box, Chip } from '@mui/material';
import { useIntl } from 'react-intl';

/**
 * Props for the FragmentCfrTranscoding component
 */
interface Props {
  /** The fragment to be transcoded to CFR (Constant Frame Rate) */
  fragment: Fragment;
}

/**
 * FragmentCfrTranscoding Component
 *
 * A component that provides an interface for transcoding video fragments to CFR
 * (Constant Frame Rate) format. This is useful for ensuring consistent frame rates
 * across video content, which can improve playback compatibility and quality.
 *
 * The component integrates with the fragments API to:
 * - Monitor the status of CFR transcoding jobs
 * - Start new CFR transcoding jobs
 * - Display job progress and completion status
 * - Provide user confirmation before starting jobs
 *
 * Features:
 * - Automatic polling of job status during execution
 * - Visual feedback for different job states (waiting, in progress, completed, failed)
 * - Confirmation dialog before starting transcoding
 * - Spanish language interface
 *
 * @example
 * ```tsx
 * <FragmentCfrTranscoding fragment={videoFragment} />
 * ```
 */
export function FragmentCfrTranscoding(props: Props) {
  const { $t } = useIntl();
  // Query to monitor the CFR transcoding job status with automatic refetching
  const statusQuery = useCfrTranscodingGetCfrTranscodingJobStatus(props.fragment.id, {
    query: {
      refetchInterval: refetchIntervalForJob,
    },
  });

  // Mutation to start a new CFR transcoding job
  const startMutation = useCfrTranscodingStartCfrTranscodingJob({
    mutation: {
      onSuccess: () => {
        // Refetch the status query after successfully starting the job
        statusQuery.refetch();
      },
    },
  });

  return (
    // Container with visual separation from other components
    <Box
      mt={2}
      sx={{
        borderTop: '1px solid #ddd',
        paddingTop: 2,
      }}
    >
      {/* JobStarter component configured for CFR transcoding */}
      <JobStarter
        query={statusQuery}
        // Start the CFR transcoding job asynchronously
        onStart={async () => {
          await startMutation.mutateAsync({ fragmentId: props.fragment.id });
        }}
        // Success state: green chip indicating completed transcoding
        successComponent={
          <Chip
            icon={<SwitchVideoIcon />}
            size="small"
            label={$t({
              id: 'fragments.export.transcodeCfrCompleted',
              defaultMessage: 'Transcodificación CFR completada',
            })}
            color="success"
          />
        }
        // Error state: red chip indicating failed transcoding
        errorComponent={
          <Chip
            icon={<SwitchVideoIcon />}
            size="small"
            label={$t({
              id: 'fragments.export.transcodeCfrFailed',
              defaultMessage: 'Transcodificación CFR fallida',
            })}
            color="error"
          />
        }
        // Progress message shown during job execution
        progressMessage={$t({
          id: 'fragments.export.transcodeCfrInProgress',
          defaultMessage: 'Transcodificación CFR en curso',
        })}
        // Button text for starting the transcoding job
        startButtonText={$t({
          id: 'fragments.export.transcodeCfrTitle',
          defaultMessage: 'Transcodificar video a CFR',
        })}
        // Icon used throughout the component (button, chips)
        icon={<SwitchVideoIcon />}
        // Confirmation dialog before starting the job
        confirmation={{
          title: $t({
            id: 'fragments.export.transcodeCfrTitle',
            defaultMessage: 'Transcodificar video a CFR',
          }),
          description: $t({
            id: 'fragments.export.transcodeCfrConfirm',
            defaultMessage: '¿Estás seguro de querer transcodificar el video a CFR?',
          }),
        }}
      />
    </Box>
  );
}
